import currency from 'currency.js';

import { getCurrencySymbol } from '../constants/currencies';
import { getCurrencyLocale } from './currency-locale';

// Currency configuration with decimal precision for each currency
const CURRENCY_CONFIG: Record<string, { decimals: number; symbol?: string }> = {
  // Zero decimal currencies
  JPY: { decimals: 0, symbol: '¥' },
  KRW: { decimals: 0, symbol: '₩' },
  VND: { decimals: 0, symbol: '₫' },
  CLP: { decimals: 0, symbol: '$' },
  COP: { decimals: 0, symbol: '$' },
  PYG: { decimals: 0, symbol: '₲' },
  RWF: { decimals: 0, symbol: 'RF' },
  UGX: { decimals: 0, symbol: 'USh' },
  VUV: { decimals: 0, symbol: 'VT' },
  XAF: { decimals: 0, symbol: 'FCFA' },
  XOF: { decimals: 0, symbol: 'CFA' },
  XPF: { decimals: 0, symbol: '₣' },

  // Three decimal currencies
  BHD: { decimals: 3, symbol: '.د.ب' },
  IQD: { decimals: 3, symbol: 'ع.د' },
  JOD: { decimals: 3, symbol: 'د.ا' },
  KWD: { decimals: 3, symbol: 'د.ك' },
  LYD: { decimals: 3, symbol: 'ل.د' },
  OMR: { decimals: 3, symbol: 'ر.ع.' },
  TND: { decimals: 3, symbol: 'د.ت' },

  // Default two decimal currencies (most common)
  USD: { decimals: 2, symbol: '$' },
  EUR: { decimals: 2, symbol: '€' },
  GBP: { decimals: 2, symbol: '£' },
  CAD: { decimals: 2, symbol: 'C$' },
  AUD: { decimals: 2, symbol: 'A$' },
  CHF: { decimals: 2, symbol: 'CHF' },
  CNY: { decimals: 2, symbol: '¥' },
  INR: { decimals: 2, symbol: '₹' },
  MXN: { decimals: 2, symbol: '$' },
  BRL: { decimals: 2, symbol: 'R$' },
};

/**
 * `new Intl.NumberFormat` is a native formatter construction (~1 ms on a
 * phone), and a list row calls it two or three times per mount — hundreds
 * of constructions when a FlatList fills its window. Formatters are
 * immutable, so one per (locale, decimals) is reused for the process.
 */
const numberFormatters = new Map<string, Intl.NumberFormat>();

function numberFormatter(locale: string, decimals: number): Intl.NumberFormat {
  const key = `${locale}|${decimals}`;
  let formatter = numberFormatters.get(key);
  if (!formatter) {
    formatter = new Intl.NumberFormat(locale, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
      useGrouping: true,
    });
    numberFormatters.set(key, formatter);
  }
  return formatter;
}

const rateFormatters = new Map<string, Intl.NumberFormat>();

/**
 * Get the number of decimal places for a currency
 */
export function getCurrencyDecimals(currencyCode: string): number {
  return CURRENCY_CONFIG[currencyCode]?.decimals ?? 2; // Default to 2 decimals
}

/**
 * Format an amount according to currency-specific decimal precision with locale-based separators
 * Uses the same logic as react-currency-input-field for consistent formatting
 */
export function formatAmountForCurrency(
  amount: number,
  currencyCode: string,
  locale?: string
): string {
  // Handle undefined or null amounts
  if (amount === undefined || amount === null) {
    amount = 0;
  }

  const currencyLocale = locale || getCurrencyLocale(currencyCode);
  const decimals = getCurrencyDecimals(currencyCode);

  try {
    // The number part only (no currency style); the symbol is placed by hand.
    const formattedNumber = numberFormatter(currencyLocale, decimals).format(
      amount
    );

    // Get the currency symbol
    const symbol = getCurrencySymbol(currencyCode);

    // Place symbol before amount with space for readability
    return `${symbol} ${formattedNumber}`;
  } catch (_error) {
    // Fallback with spacing
    const config = CURRENCY_CONFIG[currencyCode];
    const formattedNumber = amount.toFixed(decimals);

    if (config?.symbol) {
      return `${config.symbol} ${formattedNumber}`;
    }

    return `${formattedNumber} ${currencyCode}`;
  }
}

/**
 * Format an amount with both its symbol AND its ISO code, e.g.
 * "$ 40.00 USD" or "$ 1,952,856 COP".
 *
 * Used in balance contexts (cross-group balances, per-group balances)
 * where the currency must be unambiguous — many currencies share a
 * symbol (USD/CAD/AUD/MXN/COP all use "$"), so the code disambiguates.
 */
export function formatCurrencyWithCode(
  amount: number,
  currencyCode: string,
  locale?: string
): string {
  return `${formatAmountForCurrency(amount, currencyCode, locale)} ${currencyCode}`;
}

/**
 * Formats a foreign-currency exchange rate for display: at most 6
 * significant digits, locale-aware. Shared by the web `ConversionPreview`
 * (the expense form's live preview) and `ExpenseDetailPage` (the
 * persisted detail line), and by mobile's `ConversionPreview` /
 * `ConversionRateEditor`, so every client agrees on the same rounding
 * rule.
 */
export function formatExchangeRate(rate: number, locale: string): string {
  let formatter = rateFormatters.get(locale);
  if (!formatter) {
    formatter = new Intl.NumberFormat(locale, { maximumSignificantDigits: 6 });
    rateFormatters.set(locale, formatter);
  }
  return formatter.format(rate);
}

/**
 * Format a signed balance with a leading sign, a space, then the
 * symbol + amount + ISO code — e.g. "+ $ 8,167.00 USD" or
 * "- € 20.00 EUR". The magnitude is formatted from the absolute value;
 * the sign communicates direction (owed vs owe). Zero formats as "+ …";
 * callers that distinguish a settled state should handle it before
 * calling this.
 */
export function formatSignedCurrencyWithCode(
  amount: number,
  currencyCode: string,
  locale?: string
): string {
  const sign = amount < 0 ? '-' : '+';
  return `${sign} ${formatCurrencyWithCode(Math.abs(amount), currencyCode, locale)}`;
}

/**
 * Format amount as string with proper decimal places and locale-based separators
 */
export function formatAmountAsString(
  amount: number,
  currencyCode: string,
  locale: string = 'en-US'
): string {
  const decimals = getCurrencyDecimals(currencyCode);

  try {
    return numberFormatter(locale, decimals).format(amount);
  } catch (_error) {
    // Fallback to basic formatting
    return amount.toFixed(decimals);
  }
}

/**
 * Check if an amount is effectively zero for the given currency
 */
export function isEffectivelyZero(
  amount: number,
  currencyCode: string
): boolean {
  const decimals = getCurrencyDecimals(currencyCode);
  const threshold = Math.pow(10, -decimals);
  return Math.abs(amount) < threshold;
}

/**
 * Round amount to currency-specific precision using currency.js for precision
 */
export function roundToCurrencyPrecision(
  amount: number,
  currencyCode: string
): number {
  const decimals = getCurrencyDecimals(currencyCode);

  // Use currency.js for precise rounding to avoid floating point errors
  try {
    return currency(amount, { precision: decimals }).value;
  } catch (_error) {
    // Fallback to traditional rounding
    const factor = Math.pow(10, decimals);
    return Math.round(amount * factor) / factor;
  }
}

/**
 * Whether `parts` add up to `total` once each is rounded to the currency's
 * precision, compared exactly in whole minor units. A flat 0.01 tolerance lets
 * through a one-cent gap the API rejects, and fits neither JPY nor KWD.
 */
export function sumsMatchInMinorUnits(
  parts: number[],
  total: number,
  currencyCode: string
): boolean {
  const factor = Math.pow(10, getCurrencyDecimals(currencyCode));
  const toMinor = (amount: number) =>
    Math.round(roundToCurrencyPrecision(amount, currencyCode) * factor);
  const partsMinor = parts.reduce((sum, part) => sum + toMinor(part), 0);
  return partsMinor === toMinor(total);
}

/**
 * Create a currency.js instance with proper precision for the currency
 */
export function createCurrencyInstance(
  amount: number | string,
  currencyCode: string
) {
  const decimals = getCurrencyDecimals(currencyCode);

  try {
    return currency(amount, { precision: decimals });
  } catch (_error) {
    // Fallback: return a simple object with value property
    const numericAmount =
      typeof amount === 'string' ? parseFloat(amount) || 0 : amount;
    return {
      value: roundToCurrencyPrecision(numericAmount, currencyCode),
      add: (other: { value?: number } | number) =>
        createCurrencyInstance(
          numericAmount +
            (typeof other === 'object' ? other.value || 0 : other),
          currencyCode
        ),
      subtract: (other: { value?: number } | number) =>
        createCurrencyInstance(
          numericAmount -
            (typeof other === 'object' ? other.value || 0 : other),
          currencyCode
        ),
      multiply: (multiplier: number) =>
        createCurrencyInstance(numericAmount * multiplier, currencyCode),
      divide: (divisor: number) =>
        createCurrencyInstance(numericAmount / divisor, currencyCode),
    };
  }
}

/**
 * Parse a currency amount string to number with precise rounding
 */
export function parseCurrencyAmount(
  amountStr: string,
  currencyCode: string
): number {
  // Remove currency symbols and non-numeric characters except decimal points
  const cleanStr = amountStr.replace(/[^\d.-]/g, '');
  const amount = parseFloat(cleanStr) || 0;

  // Use currency.js for precise parsing
  const currencyInstance = createCurrencyInstance(amount, currencyCode);
  return currencyInstance.value;
}

/**
 * Validate currency amount for specific currency rules
 */
export function validateCurrencyAmount(
  amount: number,
  currencyCode: string
): {
  isValid: boolean;
  error?: string;
} {
  if (isNaN(amount) || !isFinite(amount)) {
    return { isValid: false, error: 'Amount must be a valid number' };
  }

  if (amount < 0) {
    return { isValid: false, error: 'Amount cannot be negative' };
  }

  const decimals = getCurrencyDecimals(currencyCode);
  const factor = Math.pow(10, decimals);
  const rounded = Math.round(amount * factor) / factor;

  if (Math.abs(amount - rounded) > Number.EPSILON) {
    return {
      isValid: false,
      error: `Amount has too many decimal places for ${currencyCode}. Maximum ${decimals} decimal${decimals === 1 ? '' : 's'} allowed.`,
    };
  }

  return { isValid: true };
}

/**
 * Format amount with locale-appropriate thousands separators
 */
export function formatAmountWithSeparators(
  amount: number,
  currencyCode: string,
  locale: string = 'en-US'
): string {
  const decimals = getCurrencyDecimals(currencyCode);

  try {
    return numberFormatter(locale, decimals).format(amount);
  } catch (_error) {
    // Fallback to basic formatting
    return amount.toFixed(decimals);
  }
}

/**
 * Parse formatted amount string (with locale-specific separators) back to number
 */
export function parseFormattedAmount(
  amountStr: string,
  locale: string = 'en-US'
): number {
  // Remove any non-numeric characters except decimal separators
  // Handle different locale decimal separators
  let cleanStr = amountStr;

  // For locales that use comma as decimal separator (e.g., de-DE, fr-FR)
  if (
    ['de', 'fr', 'es', 'it', 'pt', 'nl'].some(lang => locale.startsWith(lang))
  ) {
    // Replace dots (thousand separators) with nothing, then comma with dot
    cleanStr = cleanStr.replace(/\./g, '').replace(',', '.');
  } else {
    // For locales using dot as decimal (e.g., en-US, en-GB)
    // Remove commas (thousand separators)
    cleanStr = cleanStr.replace(/,/g, '');
  }

  // Remove any remaining non-numeric characters except dots and minus
  cleanStr = cleanStr.replace(/[^\d.-]/g, '');

  return parseFloat(cleanStr) || 0;
}

// Export alias for backward compatibility
export const formatAmountWithCommas = formatAmountWithSeparators;
